import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  TextField,
  InputAdornment,
  Chip,
  Pagination,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Assessment as AssessmentIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Search as SearchIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useQuery } from "@apollo/client";
import {
  GET_CHECK_BASED_EVIDENCES,
  GET_EVIDENCES_BY_CHECK,
} from "../../apis/integrated-evidence/aiEvidenceApis";
import InformationStepsCard from "../../components/InformationStepsCard/InformationStepsCard";
import EntityDetailDialog from "./EntityDetailDialog";
import { IntegratedEvidenceApprovalStatusChip } from "./IntegratedEvidenceApprovalStatusChip";
import { entityTypeRegistry } from "../../lib/EntityTypeRegistry";
import ReusableAccordion from "../../components/ReusableAccordion/ReusableAccordion";
import ReusableTable from "../../components/ReusableTable/ReusableTable";

const getEntityIcon = (type) => {
  const IconComponent = entityTypeRegistry.getIcon(type);
  return IconComponent ? <IconComponent /> : <AutoAwesomeIcon />;
};

const StatCard = ({ title, value, icon, color, bgColor, iconColor, tooltipTitle }) => (
  <Card
    elevation={0}
    sx={{
      p: 2,
      borderRadius: "12px",
      border: "1px solid #E8EAED",
      backgroundColor: "#FFFFFF",
      flex: 1,
      transition: "all 0.3s ease",
      "&:hover": {
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        transform: "translateY(-2px)",
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <Box>
        <Typography variant="h5" fontWeight="600" color={color || "text.primary"}>
          {value}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" color="textSecondary">
            {title}
          </Typography>
          {tooltipTitle && (
            <Tooltip title={tooltipTitle} arrow>
              <InfoIcon sx={{ fontSize: 14, color: "text.secondary", cursor: "pointer" }} />
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: bgColor || "#f0f7ff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {React.cloneElement(icon, {
          sx: { color: iconColor || "primary.main", fontSize: 20 },
        })}
      </Box>
    </Box>
  </Card>
);

const EvidenceReviewStatusSummary = ({ pendingCount, approvedCount, rejectedCount }) => {
  const p = pendingCount ?? 0;
  const a = approvedCount ?? 0;
  const r = rejectedCount ?? 0;
  return (
    <Box sx={{ px: 1, pb: 2, pt: 0.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
        <Chip
          size="small"
          label={`${p} Pending`}
          sx={{
            bgcolor: "#e3f2fd",
            color: "#1565c0",
            fontWeight: 600,
            fontSize: "0.75rem",
            height: 26,
            borderRadius: "999px",
          }}
        />
        <Chip
          size="small"
          label={`${r} Rejected`}
          sx={{
            bgcolor: "#ffebee",
            color: "#c62828",
            fontWeight: 600,
            fontSize: "0.75rem",
            height: 26,
            borderRadius: "999px",
          }}
        />
        <Chip
          size="small"
          label={`${a} Approved`}
          sx={{
            bgcolor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: 600,
            fontSize: "0.75rem",
            height: 26,
            borderRadius: "999px",
          }}
        />
      </Box>
      <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
        {a} approved · {r} rejected · {p} pending
      </Typography>
    </Box>
  );
};

const CheckEvidenceTable = ({ checkId, pendingCount, approvedCount, rejectedCount, openDetailDialog }) => {
  const [innerPage, setInnerPage] = useState(1);
  const pageSize = 20;

  const { data, loading } = useQuery(GET_EVIDENCES_BY_CHECK, {
    variables: { checkId, page: innerPage, pageSize },
    fetchPolicy: "network-only",
  });

  // Helper function to truncate entity name with ellipsis
  const truncateEntityName = (name, maxLength = 60) => {
    if (!name) return "Unknown Entity";
    return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
  };

  const columns = [
    {
      field: "entity",
      label: "Entity Resource",
      width: "400px",
      align: "left",
      render: (row) => (
        <Tooltip title={row.entityName || "Unknown Entity"} arrow>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, overflow: "hidden" }}>
            <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center", flexShrink: 0 }}>
              {getEntityIcon(row.entityType)}
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#1976d2",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              onClick={() => openDetailDialog(row.entityId, row.entityType)}
            >
              {truncateEntityName(row.entityName)}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "status",
      label: "Status",
      width: "180px",
      align: "left",
      render: (row) => (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5, py: 0.25 }}>
          <IntegratedEvidenceApprovalStatusChip approvalStatus={row.approvalStatus} />
          <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.3 }}>
            {row.approvalStatus === "approved" && "Accepted as evidence"}
            {row.approvalStatus === "rejected" && "Excluded from audit record"}
            {row.approvalStatus === "pending" && "Awaiting your review"}
          </Typography>
        </Box>
      ),
    },
  ];

  // Custom renderCell to support column.render functions
  const renderCell = (row, column) => {
    if (column.render) {
      return column.render(row);
    }
    return row[column.field];
  };

  const totalPages = Math.ceil((data?.getEvidencesByCheck?.totalCount || 0) / pageSize);

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <EvidenceReviewStatusSummary
        pendingCount={pendingCount}
        approvedCount={approvedCount}
        rejectedCount={rejectedCount}
      />
      <ReusableTable
        columns={columns}
        data={data?.getEvidencesByCheck?.entities || []}
        loading={loading}
        renderCell={renderCell}
        sx={{
          "& .MuiTableCell-root": {
            padding: "10px 16px",
            borderBottom: "1px solid #e5e7eb",
            fontSize: "0.875rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: "#f3f4f6",
            fontWeight: 800,
            color: "#4b5563",
            textTransform: "uppercase",
            fontSize: "0.7rem",
            letterSpacing: "0.05em",
            py: 1.5,
            borderBottom: "2px solid #d1d5db",
          },
          "& .MuiTableRow-root:hover": {
            backgroundColor: "#f9fafb",
          },
          border: "1px solid #e5e7eb",
          borderRadius: "4px",
          overflow: "hidden",
        }}
      />
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2, borderTop: "1px solid #f0f0f0" }}>
          <Pagination
            size="small"
            count={totalPages}
            page={innerPage}
            onChange={(e, v) => setInnerPage(v)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

const AIEvidenceHome = () => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFullyReviewed, setShowFullyReviewed] = useState(false);
  const [expandedCheckId, setExpandedCheckId] = useState(null);

  const [selectedEntity, setSelectedEntity] = useState({ id: null, type: null });
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const { data, loading, refetch } = useQuery(GET_CHECK_BASED_EVIDENCES, {
    variables: {
      page,
      pageSize,
      search: searchQuery || null,
      showFullyReviewed,
    },
    fetchPolicy: "network-only",
  });

  const openDetailDialog = (id, type) => {
    setSelectedEntity({ id, type });
    setDetailModalOpen(true);
  };

  const responseData = data?.getCheckBasedEvidences;
  const stats = responseData?.stats || {};
  const checks = responseData?.checks || [];
  const totalPages = Math.ceil((responseData?.totalCount || 0) / pageSize);



  return (
    <Container maxWidth={false} sx={{ paddingTop: "2%", paddingBottom: "2%" }}>
      <InformationStepsCard
        icon={<AutoAwesomeIcon sx={{ color: "primary.main", fontSize: 40 }} />}
        heading="What should I do on this page?"
        defaultExpanded={false}
        steps={[
          { 
            title: "View Checks", 
            description: "Browse compliance checks AI has evaluated. Use search to filter by name." 
          },
          { 
            title: "Expand & Review", 
            description: "Click the arrow to expand a check and see the entities (users, assets, configs) identified as evidence." 
          },
          { 
            title: "View Details", 
            description: "Click any entity name to inspect its full content, attachments, and metadata." 
          },
          { 
            title: "Approve or reject", 
            description: "Open an entity to read full content, then use Approve or Reject on each linked check in the detail panel." 
          },
          { 
            title: "Status summary", 
            description: "Counts above the table show how many entities are pending, approved, or rejected for this check." 
          },
        ]}
      />

      {/* Stats row */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <StatCard
          title="Total Assessed Checks"
          value={stats.totalLinkableChecks || 0}
          icon={<AssessmentIcon />}
          color="#1e293b"
          bgColor="#f1f5f9"
          iconColor="#64748b"
          tooltipTitle="Total number of unique compliance checks containing evaluated evidence"
        />
        <StatCard
          title="Awaiting Review"
          value={stats.awaitingReview || 0}
          icon={<HourglassEmptyIcon />}
          color="#b45309"
          bgColor="#fff7ed"
          iconColor="#f59e0b"
          tooltipTitle="Number of checks with at least one piece of evidence pending review"
        />
        <StatCard
          title="Fully Approved"
          value={stats.fullyApproved || 0}
          icon={<CheckCircleIcon />}
          color="#15803d"
          bgColor="#f0fdf4"
          iconColor="#22c55e"
          tooltipTitle="Number of checks with all evidence approved or awaiting review (no rejections)"
        />
      </Box>

      {/* Main Card */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2, bgcolor: "#fafafa", borderBottom: "1px solid #ddd", display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 250 }}>
              <TextField
                placeholder="Search Check Name..."
                size="small"
                fullWidth
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={showFullyReviewed}
                  onChange={(e) => { setShowFullyReviewed(e.target.checked); setPage(1); }}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Show Approved Evidences
                </Typography>
              }
            />
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : checks.length === 0 ? (
            <Typography sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
              No checks found matching the criteria.
            </Typography>
          ) : (
            <Box sx={{ p: 2 }}>
              {checks.map((check) => {
                const isExpanded = expandedCheckId === check.checkId;

                return (
                  <ReusableAccordion
                    key={check.checkId}
                    expanded={isExpanded}
                    onChange={() => setExpandedCheckId(isExpanded ? null : check.checkId)}
                    summary={
                      <Box sx={{ display: "flex", flexDirection: "column", py: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight={700} color="#1e293b">
                          {check.checkName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {check.totalEntities} entities acting as evidence
                        </Typography>
                      </Box>
                    }
                    details={
                      <Box sx={{ p: 1 }}>
                        {/* Check Controls Mapping */}
                        {check.mappedControls && check.mappedControls.length > 0 && (
                          <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", px: 1 }}>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase", mr: 1 }}>
                              Mapped Controls:
                            </Typography>
                            {check.mappedControls.map((ctrl) => (
                              <Chip 
                                key={ctrl.id} 
                                label={`${ctrl.name}${ctrl.domain ? ` (${ctrl.domain})` : ""}`} 
                                size="small" 
                                variant="outlined" 
                                sx={{ borderRadius: "4px", fontSize: "0.7rem", height: "20px" }}
                              />
                            ))}
                          </Box>
                        )}

                        {/* Check Description (Moved here for better UX) */}
                        {check.checkDescription && (
                          <Box sx={{ mb: 2, px: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", borderLeft: "3px solid #e2e8f0", pl: 1.5, py: 0.5 }}>
                              {check.checkDescription}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Evidence Table */}
                        <CheckEvidenceTable
                          checkId={check.checkId}
                          pendingCount={check.pendingCount}
                          approvedCount={check.approvedCount}
                          rejectedCount={check.rejectedCount}
                          openDetailDialog={openDetailDialog}
                        />
                      </Box>
                    }
                  />
                );
              })}
              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                  <Pagination count={totalPages} page={page} color="primary" onChange={(e, v) => setPage(v)} />
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <EntityDetailDialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        entityId={selectedEntity.id}
        entityType={selectedEntity.type}
        onActionComplete={() => refetch()}
      />
    </Container>
  );
};

export default AIEvidenceHome;
